import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { History, Undo, CompareArrows } from '@mui/icons-material';
import { RecipeV2Detail, RecipeVersion, RecipeComponent, ApprovalStatus } from '../types';
import { UserContext } from '../../../components/shared/useUser';
import useRecipeV2Api from '../hooks/useRecipeV2Api';

interface VersionsTabProps {
  recipe: RecipeV2Detail;
}

const approvalColor: Record<ApprovalStatus, 'success' | 'warning' | 'info' | 'error'> = {
  [ApprovalStatus.Approved]: 'success',
  [ApprovalStatus.PendingApproval]: 'warning',
  [ApprovalStatus.Draft]: 'info',
  [ApprovalStatus.Rejected]: 'error',
};

type DiffMode = 'sideBySide' | 'inline';

interface DiffEntry {
  description: string;
  type: 'added' | 'removed' | 'changed';
  oldValue?: string;
  newValue?: string;
}

function computeDiff(oldComponents: RecipeComponent[], newComponents: RecipeComponent[]): DiffEntry[] {
  const entries: DiffEntry[] = [];
  const oldMap = new Map(oldComponents.map((c) => [c.componentItemNumber, c]));
  const newMap = new Map(newComponents.map((c) => [c.componentItemNumber, c]));

  Array.from(newMap.entries()).forEach(([key, newC]) => {
    const oldC = oldMap.get(key);
    if (!oldC) {
      entries.push({ description: newC.componentDescription, type: 'added', newValue: `${newC.quantity} ${newC.uom}` });
    } else if (oldC.quantity !== newC.quantity || oldC.weight !== newC.weight) {
      entries.push({
        description: newC.componentDescription,
        type: 'changed',
        oldValue: `${oldC.quantity} ${oldC.uom} (${oldC.weight}g)`,
        newValue: `${newC.quantity} ${newC.uom} (${newC.weight}g)`,
      });
    }
  });

  Array.from(oldMap.entries()).forEach(([key, oldC]) => {
    if (!newMap.has(key)) {
      entries.push({ description: oldC.componentDescription, type: 'removed', oldValue: `${oldC.quantity} ${oldC.uom}` });
    }
  });

  return entries;
}

const diffColor: Record<string, string> = {
  added: 'success.light',
  removed: 'error.light',
  changed: 'warning.light',
};

const VersionsTab = ({ recipe }: VersionsTabProps) => {
  const { t } = useTranslation('recipeV2');
  const { hasPermissionTo } = useContext(UserContext);
  const api = useRecipeV2Api();
  const canRollback = hasPermissionTo(['RecipeV2VersionRollback']);

  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<[number | null, number | null]>([null, null]);
  const [diffMode, setDiffMode] = useState<DiffMode>('sideBySide');

  const handleRollback = async (versionNumber: number) => {
    try {
      await api.rollbackVersion(recipe.id, versionNumber);
    } catch { /* noop */ }
  };

  const handleCompareClick = (versionA: number, versionB: number) => {
    setSelectedVersions([versionA, versionB]);
    setCompareDialogOpen(true);
  };

  const getVersionSnapshot = (vNum: number): RecipeComponent[] => {
    const v = recipe.versions.find((ver) => ver.versionNumber === vNum);
    return v?.componentSnapshot ?? [];
  };

  const diffEntries = selectedVersions[0] !== null && selectedVersions[1] !== null
    ? computeDiff(getVersionSnapshot(selectedVersions[0]), getVersionSnapshot(selectedVersions[1]))
    : [];

  return (
    <Stack spacing={2}>
      <Paper variant="outlined"
        sx={{ p: 2 }}>
        <Typography variant="textXL"
          gutterBottom>
          <History sx={{ verticalAlign: 'middle', mr: 1 }} />
          {t('versions.title')}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('version')}</TableCell>
                <TableCell>{t('versions.description')}</TableCell>
                <TableCell>{t('versions.changedBy')}</TableCell>
                <TableCell>{t('versions.changedAt')}</TableCell>
                <TableCell>{t('versions.approvalStatus')}</TableCell>
                <TableCell>{t('versions.approvedBy')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {recipe.versions.map((version, idx) => (
                <TableRow
                  key={version.id}
                  sx={{
                    bgcolor: version.versionNumber === recipe.currentVersion ? 'action.selected' : undefined,
                  }}
                >
                  <TableCell>
                    <Stack direction="row"
                      alignItems="center"
                      spacing={1}>
                      <Typography fontWeight={600}>v{version.versionNumber}</Typography>
                      {version.versionNumber === recipe.currentVersion && (
                        <Chip label={t('versions.current')}
                          size="small"
                          color="primary" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{version.changeDescription}</TableCell>
                  <TableCell>{version.changedBy}</TableCell>
                  <TableCell>{version.changedAt}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(`versions.${version.approvalStatus.charAt(0).toLowerCase() + version.approvalStatus.slice(1)}`)}
                      size="small"
                      color={approvalColor[version.approvalStatus] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell>{version.approvedBy ?? '—'}</TableCell>
                  <TableCell>
                    <Stack direction="row"
                      spacing={0.5}>
                      {canRollback && version.versionNumber !== recipe.currentVersion && (
                        <Button size="md"
                          startIcon={<Undo />}
                          variant="secondary"
                          onClick={() => handleRollback(version.versionNumber)}>
                          {t('versions.rollback')}
                        </Button>
                      )}
                      {idx < recipe.versions.length - 1 && (
                        <Button size="md"
                          startIcon={<CompareArrows />}
                          variant="secondary"
                          onClick={() => handleCompareClick(
                            recipe.versions[idx + 1].versionNumber,
                            version.versionNumber,
                          )}>
                          {t('versions.compare')}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Diff dialog */}
      <Dialog open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle>
          <Stack direction="row"
            alignItems="center"
            justifyContent="space-between">
            <Typography variant="textLG">
              {t('versions.compareTitle')} — v{selectedVersions[0]} → v{selectedVersions[1]}
            </Typography>
            <ToggleButtonGroup
              value={diffMode}
              exclusive
              onChange={(_, v) => v && setDiffMode(v)}
              size="small"
            >
              <ToggleButton value="sideBySide">{t('versions.sideBySide')}</ToggleButton>
              <ToggleButton value="inline">{t('versions.inline')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {diffEntries.length === 0 ? (
            <Typography color="text.secondary"
              sx={{ py: 3, textAlign: 'center' }}>
              {t('versions.noChanges')}
            </Typography>
          ) : diffMode === 'inline' ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('ingredient')}</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {diffEntries.map((entry, i) => (
                  <TableRow key={i}
                    sx={{ bgcolor: diffColor[entry.type] }}>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(`versions.${entry.type}`)}
                        size="small"
                        color={entry.type === 'added' ? 'success' : entry.type === 'removed' ? 'error' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      {entry.type === 'changed' && (
                        <Typography variant="textSM">
                          {entry.oldValue} → {entry.newValue}
                        </Typography>
                      )}
                      {entry.type === 'added' && <Typography variant="textSM">{entry.newValue}</Typography>}
                      {entry.type === 'removed' && <Typography variant="textSM"
                        sx={{ textDecoration: 'line-through' }}>{entry.oldValue}</Typography>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Stack direction="row"
              spacing={2}>
              <Box flex={1}>
                <Typography variant="textSM"
                  fontWeight={600}
                  gutterBottom>v{selectedVersions[0]} (Previous)</Typography>
                <Table size="small">
                  <TableBody>
                    {diffEntries.map((entry, i) => (
                      <TableRow key={i}
                        sx={{ bgcolor: entry.type === 'removed' ? 'error.light' : entry.type === 'changed' ? 'warning.light' : undefined }}>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell>{entry.oldValue ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Box flex={1}>
                <Typography variant="textSM"
                  fontWeight={600}
                  gutterBottom>v{selectedVersions[1]} (Current)</Typography>
                <Table size="small">
                  <TableBody>
                    {diffEntries.map((entry, i) => (
                      <TableRow key={i}
                        sx={{ bgcolor: entry.type === 'added' ? 'success.light' : entry.type === 'changed' ? 'warning.light' : undefined }}>
                        <TableCell>{entry.description}</TableCell>
                        <TableCell>{entry.newValue ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}
            variant="secondary">{t('actions.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default VersionsTab;
